import React from 'react';
import { Link } from 'react-router-dom';
import { FaLeaf, FaHandHoldingHeart, FaTruck, FaRecycle, FaAward, FaSeedling } from 'react-icons/fa';
import { GiTomato, GiCarrot, GiCabbage, GiBroccoli } from 'react-icons/gi';

const BrandPage = () => {
  const values = [
    { icon: <FaLeaf className="text-3xl text-green-600" />, title: '100% Natural', desc: 'No pesticides or chemicals – just pure nature.' },
    { icon: <FaHandHoldingHeart className="text-3xl text-green-600" />, title: 'Ethical Sourcing', desc: 'Fair trade practices with local farmers.' },
    { icon: <FaTruck className="text-3xl text-green-600" />, title: 'Farm to Table', desc: 'Harvested at peak ripeness, delivered fresh.' },
    { icon: <FaRecycle className="text-3xl text-green-600" />, title: 'Eco-Friendly', desc: 'Plastic-free packaging and zero waste.' },
  ];

  const categories = [
    { name: 'Leafy Greens', icon: <GiCabbage className="text-5xl text-green-600" />, link: '/products?category=leafy' },
    { name: 'Root Vegetables', icon: <GiCarrot className="text-5xl text-orange-500" />, link: '/products?category=root' },
    { name: 'Tomatoes & Peppers', icon: <GiTomato className="text-5xl text-red-500" />, link: '/products?category=tomato' },
    { name: 'Broccoli & Cauliflower', icon: <GiBroccoli className="text-5xl text-green-600" />, link: '/products?category=broccoli' },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-700 to-green-800 text-white py-24">
        <div className="absolute inset-0 overflow-hidden">
          <img src="/brand-hero.jpg" alt="Freshigo Vegetables" className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fadeInDown">Freshigo Vegetables</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-green-100">
            Freshness you can trust, quality you can taste.
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-green-400 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-500"></div>
              <img
                src="https://tse3.mm.bing.net/th/id/OIP.DGFWuUIgrlHJj3x1FfRVUAHaF7?w=1280&h=1024&rs=1&pid=ImgDetMain&o=7&rm=3"
                alt="Our brand story"
                className="relative w-full h-auto rounded-lg shadow-2xl transform group-hover:scale-105 transition duration-500"
              />
            </div>
            <div className="space-y-6 animate-fadeInUp">
              <h2 className="text-4xl font-bold text-green-800 mb-4">Our Brand</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Freshigo Vegetables was born from a passion for fresh, healthy food. We believe that everyone deserves access to premium vegetables grown with care for both people and the planet. Our brand represents a commitment to quality, sustainability, and community.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                From our farmer partners to your family's table, we ensure every step of the journey upholds the highest standards. When you choose Freshigo, you're not just buying vegetables – you're supporting a movement toward a greener, healthier world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-green-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition duration-300 text-center"
              >
                <div className="flex justify-center mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-green-700 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Product Range</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our diverse selection of fresh vegetables.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {categories.map((cat, index) => (
              <Link
                to={cat.link}
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition duration-300 text-center group"
              >
                <div className="flex justify-center mb-4 group-hover:scale-110 transition">
                  {cat.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-800">{cat.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Commitment */}
      <section className="bg-green-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <FaSeedling className="text-5xl mx-auto mb-4" />
          <h2 className="text-4xl font-bold mb-4">Committed to a Greener Future</h2>
          <p className="text-xl text-green-200 max-w-3xl mx-auto mb-8">
            We're on a mission to reduce waste, support sustainable farming, and protect our planet for generations to come.
          </p>
          <Link
            to="/sustainability"
            className="inline-flex items-center px-8 py-4 bg-white text-green-700 font-semibold rounded-lg shadow-lg hover:bg-green-100 transform hover:scale-105 transition duration-300"
          >
            Learn More About Our Initiatives
          </Link>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-green-800 mb-4">Experience the Freshigo Difference</h2>
          <p className="text-lg text-gray-600 mb-8">Browse our catalog and taste the freshness.</p>
          <Link
            to="/products"
            className="inline-flex items-center px-8 py-4 bg-green-600 text-white font-semibold rounded-lg shadow-lg hover:bg-green-700 transform hover:scale-105 transition duration-300"
          >
            Shop Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BrandPage;